import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { Cita } from "../models";
import { databases, ID, storage } from "../config/appwrite";
import { environment } from "src/environments/environment";
import { Query } from "appwrite";
import {
  CancelarCitaResponse,
  ReprogramarCitaResponse,
} from "../models/saldo.model";

@Injectable({
  providedIn: "root",
})
export class CitaService {
  private collectionId = "citas";
  private collectionHistorico = "citas_historico";
  private DATABASE_NAME = environment.databaseName;

  constructor(private httpClient: HttpClient) {}

  //VALIDACIÓN SI UNA CITA EXISTE
  // SE BUSCA POR:
  // ID ESPECIALISTA
  // FECHA CITA
  // ID ESTADO CITA: cancelada, reprogramada
  getCitasAndValidate(idEspecialista: string, fechaCita: string) {
    return databases
      .listDocuments<Cita>(this.DATABASE_NAME, this.collectionId, [
        Query.equal("idEspecialista", idEspecialista),
        Query.equal("fechaCita", fechaCita),
        Query.notEqual("idEstadoCita", "cancelada"),
        Query.notEqual("idEstadoCita", "reprogramada"),
      ])
      .then((response) => response.documents);
  }

  // CREA UNA NUEVA CITA
  createDocument(data: Cita) {
    return databases.createDocument(
      this.DATABASE_NAME,
      this.collectionId,
      ID.unique(),
      data,
    );
  }

  // OBTIENE DE CITAS DE UN ESPECIALISTA
  getDocumentsByEspecialistagreaterThanCurrentDate(
    idEspecialista: string,
    dateRange: { start: Date; end: Date },
  ): Promise<Cita[]> {
    // Extraemos start y end del objeto dateRange
    const { start, end } = dateRange;

    // Convertimos las fechas a ISO string
    const startISO = start.toISOString();
    const endISO = end.toISOString();

    return databases
      .listDocuments<Cita>(
        this.DATABASE_NAME,
        this.collectionId, //citas
        [
          Query.equal("idEspecialista", idEspecialista),
          Query.greaterThanEqual("fechaCita", startISO),
          Query.lessThan("fechaCita", endISO),
          Query.notEqual("idEstadoCita", "reprogramada"),
          Query.notEqual("idEstadoCita", "cancelada"),
          Query.select(["fechaCita", "duracionCita", "idEstadoCita.$id"]),
        ],
      )
      .then((response) => response.documents);
  }

  // LISTADO DE CITAS DESDE EL DIA DE HOY
  getDocumentsThanCurrentDate(): Promise<Cita[]> {
    let firstDate = new Date();
    firstDate.setHours(0, 0, 0, 0);
    return databases
      .listDocuments<Cita>(this.DATABASE_NAME, this.collectionId, [
        Query.greaterThanEqual("fechaCita", firstDate.toISOString()),
      ])
      .then((response) => response.documents);
  }

  // Este metodo se encarga de obtener las citas de un paciente excluyendo las que no son relevantes para "Mis Citas"
  getDocumentsbyPaciente(idPaciente: string): Promise<Cita[]> {
    return databases
      .listDocuments<Cita>(
        this.DATABASE_NAME,
        this.collectionId, //citas
        [
          Query.equal("idPaciente", idPaciente),
          Query.orderDesc("fechaCita"),
          Query.notEqual("idEstadoCita", "cancelada"),
          Query.notEqual("idEstadoCita", "cerrada"),
          Query.notEqual("idEstadoCita", "terminada"),
          Query.notEqual("idEstadoCita", "reprogramada"),
          Query.notEqual("idEstadoCita", "noasistio"),
          Query.limit(6),
        ],
      )
      .then((response) => response.documents);
  }

  // OBTENE LAS CITAS DE UN PACIENTE, EXCLUYENDO LAS CERRADAS, TERMINADAS O REPROGRAMADAS
  getDocumentsbyPaciente2(idPaciente: string) {
    /*  TODO: REVISAR QUE ESTA OCURRIENDO CON LAS CONSULTAS 
        Se crea este método 2 para validar 
    */

    return databases.listDocuments<any>(
      this.DATABASE_NAME,
      this.collectionId, //citas
      [
        Query.equal("idPaciente", idPaciente),
        Query.orderDesc("fechaCita"),
        Query.notEqual("idEstadoCita", "cancelada"),
        Query.select(["$id", "fechaCita", "idEspecialista.banco"]),

        Query.limit(1),
      ],
    );
  }

  /**
   * TRAE VARIAS CITAS POR SU ID, EN UNA SOLA CONSULTA.
   *
   * La usa la pantalla de pagos para mostrar de qué cita salió cada movimiento
   * (fecha, hora, especialista, consultorio) en vez del id crudo.
   *
   * Sin Query.select a propósito: así Appwrite expande las relaciones
   * idEspecialista e idSede, que es justo lo que hay que mostrar.
   */
  getCitasByIds(ids: string[]): Promise<any[]> {
    if (!ids.length) {
      return Promise.resolve([]);
    }
    return databases
      .listDocuments<any>(this.DATABASE_NAME, this.collectionId, [
        Query.equal("$id", ids),
        Query.limit(100),
      ])
      .then((response) => response.documents);
  }

  //TRAE LA ÚLTIMA CITA DEL PACIENTE PARA OBTENER EL ESPECIALISTA QUE LO ATENDIO.
  getLastCita(idPaciente: string): Promise<Cita | null> {
    let firstDate = new Date();
    firstDate.setHours(0, 0, 0, 0);
    return databases
      .listDocuments<Cita>(this.DATABASE_NAME, this.collectionId, [
        Query.equal("idPaciente", idPaciente),
        Query.orderDesc("fechaCita"),
        Query.limit(1), // Limita el resultado a 1 documento
        Query.select(["idEspecialista.$id", "idEspecialista.nombre"]),
      ])
      .then((response) => response.documents[0] || null);
  }

  //  OBTIENE DE CITAS DE UN ESPECIALISTA
  getDocumentsByEspecialista(idEspecialista: string): Promise<Cita[]> {
    return databases
      .listDocuments<Cita>(this.DATABASE_NAME, this.collectionId, [
        Query.equal("idEspecialista", idEspecialista),
      ])
      .then((response) => response.documents);
  }

  // OBTIENE UNA CITA POR ID
  getDocument(idDocument: string) {
    return databases.getDocument(
      this.DATABASE_NAME,
      this.collectionId,
      idDocument,
    );
  }

  //LISTADO DE CITAS
  getDocuments(): Promise<Cita[]> {
    return databases
      .listDocuments<Cita>(this.DATABASE_NAME, this.collectionId)
      .then((response) => response.documents);
  }

  // CAMBIA ESTADO DE UNA CITA
  /**
   * CANCELA UNA CITA Y CONVIERTE SU PAGO EN SALDO A FAVOR.
   *
   * Pasa por la API y NO por Appwrite a propósito. Escribir
   * idEstadoCita='cancelada' directo en Appwrite deja el pago huérfano: el
   * webhook de Appwrite sobre `citas` solo entrega eventos de create, así que
   * el backend nunca se entera de la cancelación y el saldo no se acredita.
   * Ese fue el bug de agosto 2026 (ver README, sección SALDOS A FAVOR).
   *
   * El backend valida que la cita pertenezca al paciente y responde 422 si no.
   * Es idempotente: un doble click no acredita el saldo dos veces.
   */
  cancelarCita(
    idCita: string,
    idPaciente: string,
    motivo?: string
  ): Promise<CancelarCitaResponse> {
    return firstValueFrom(
      this.httpClient.post<CancelarCitaResponse>(
        environment.apiPsicologa + "/cancelarCita",
        { idCita, idPaciente, motivo }
      )
    );
  }

  /**
   * REPROGRAMA UNA CITA: la marca como reprogramada y compensa lo pagado.
   *
   * Reprogramar no mueve la fecha: mata la cita y el paciente agenda otra
   * nueva. Por eso hay que compensar lo que ya pagó, o se le cobra dos veces.
   *
   * Esta única llamada reemplaza lo que antes eran dos escrituras directas a
   * Appwrite (changeEstadoCita + devolverPaquete). El backend decide la rama:
   * pago aprobado → saldo a favor; pago con paquete → devuelve la sesión.
   *
   * `motivo` es OBLIGATORIO aquí (en cancelarCita es opcional). Responde 422 si
   * falta, si la cita no es del paciente, o si no se cumple la antelación
   * mínima de 4 horas, que el backend valida por su cuenta.
   */
  reprogramarCita(
    idCita: string,
    idPaciente: string,
    motivo: string
  ): Promise<ReprogramarCitaResponse> {
    return firstValueFrom(
      this.httpClient.post<ReprogramarCitaResponse>(
        environment.apiPsicologa + "/reprogramarCita",
        { idCita, idPaciente, motivo }
      )
    );
  }

  /**
   * ⚠️ NO USAR PARA CANCELAR NI REPROGRAMAR. Para eso están cancelarCita() y
   * reprogramarCita(), que además compensan lo que el paciente ya pagó.
   * Este método sirve para los demás cambios de estado (confirmada, etc.).
   */
  changeEstadoCita(item: any) {
    return databases.updateDocument(
      this.DATABASE_NAME,
      this.collectionId,
      item.$id,
      item,
    );
  }

  // ACTUALIZA UNA CITA
  updateDocument(idCita: string, data: any) {
    data.$databaseId = undefined;
    data.$collectionId = undefined;
    return databases.updateDocument(
      this.DATABASE_NAME,
      this.collectionId,
      idCita,
      data,
    );
  }

  // ####################################################################
  // CITAS HISTORICO

  getCitasByPacienteHistorico(idPaciente: string): Promise<any[]> {
    return databases
      .listDocuments(this.DATABASE_NAME, this.collectionHistorico, [
        Query.equal("idPaciente", idPaciente),
        Query.notEqual("idEstadoCita", "cancelada"),
        Query.orderDesc("fechaCita"),
        Query.select(["idCita.$id", "fechaCita", "idEstadoCita.$id"]),
      ])
      .then((response) => response.documents);
  }

  /**
   *
   * @param idPaciente
   * @returns
   */
  getCitasTerminadas(idPaciente: string): Promise<Cita[]> {
    return databases
      .listDocuments<Cita>(this.DATABASE_NAME, this.collectionId, [
        Query.orderDesc("$createdAt"),
        Query.or([
          Query.equal("idEstadoCita", "terminada"),
          Query.equal("idEstadoCita", "cerrada"),
        ]),

        Query.equal("idPaciente", idPaciente),
        Query.limit(1),
      ])
      .then((response) => response.documents);
  }

  /**
   *
   * @param idCita
   * @returns
   */
  getLastCitaHistorico(idCita: string): Promise<Cita[]> {
    return databases
      .listDocuments<Cita>(this.DATABASE_NAME, this.collectionHistorico, [
        Query.orderDesc("$createdAt"),
        Query.equal("idCita", idCita),
        Query.limit(1),
      ])
      .then((response) => response.documents);
  }
}
